/**
 * CrudDataGrid Component
 * 
 * This component is responsible for rendering a data grid with CRUD (Create, Read, Update, Delete) functionalities.
 * It includes features such as:
 * - Adding new rows to the grid.
 * - Editing existing rows with validation.
 * - Deleting rows from the grid.
 * - Saving or canceling changes to rows.
 * 
 * Props:
 * - initialColumns: Array of column definitions for the grid.
 * - initialRows: Array of initial row data.
 * - name: Name identifier for the grid.
 * - onRowChange: Callback function to handle row data changes.
 * - disabled: Boolean to disable grid interactions.
 * - maxLimit: Maximum number of rows allowed in the grid.
 * - height: Height of the grid container.
 * - addLabel: Custom label for the "Add" button.
 * 
 * Dependencies:
 * - React hooks (useState, useEffect) for state management and lifecycle handling.
 * - Material-UI components for layout and styling.
 * - Utility functions for row validation and data manipulation.
 * 
 * Note:
 * - The component supports both editable and non-editable modes based on the `disabled` prop.
 * - Validation ensures that at least one column in a row is filled before saving.
 */

import * as React from 'react';
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { ToastContainer, toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridSlots,
} from '@mui/x-data-grid';
import {
  randomCreatedDate,
  randomTraderName,
  randomId,
  randomArrayItem,
} from '@mui/x-data-grid-generator';
import { useTimeout } from '@mui/x-data-grid/internals';
import DownloadIcon from '@mui/icons-material/Download';
import { useGridApiRef } from '@mui/x-data-grid';


//#this grid allow users to perform crud operations
const UDataGrid = (props) => {

  //#region props
  const { initialColumns, initialRows, name,readOnly, onRowChange, disabled, maxLimit, height,addLabel } = props;

  //#region variables
  const apiRef = useGridApiRef();
  const rowsWithId = Array.isArray(initialRows) ? initialRows.map((row, index) => ({ ...row, id: index })) : [];
  const [rows, setRows] = React.useState(rowsWithId);
  const [rowModesModel, setRowModesModel] = React.useState({});

  //#region funcitons
  const validateRow = (rowData) => {
    let isValid = true;
    isValid = Object.keys(rowData)
              .filter(item => item !== "id" && item !== "isNew")
              .some(key => rowData[key] !== "");
    return isValid;
  };

  //#region click events
  const handleAddClick = () => {
    const id = randomId();
    const newRow = {
      id,
      ...columns?.reduce((acc, item) => {
        acc[item.field] = '';
        return acc;
      }, {})
    };
    // Create a new object based on newRow
    const modifiedNewRow = { ...newRow };
    // Remove the 'actions' field if it exists
    delete modifiedNewRow.actions;
    // Add the 'isNew' field set to false
    modifiedNewRow.isNew = false;
    setRows(oldRows => [...oldRows, modifiedNewRow]);
    /* onRowChange(name, [rows, modifiedNewRow]); */
    setRowModesModel(oldModel => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
    }));
  };

  const handleDownload = () => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv(); 
    }
  };

  //#region grid events
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (rowData) => () => {
    setRowModesModel({ ...rowModesModel, [rowData?.id]: { mode: GridRowModes.View } });
  
  };


  const handleDeleteClick = (id) => () => {
    /* onRowChange(name, rows.filter((row) => row.id !== id)); */
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      /* onRowChange(name, rows.filter((row) => row.id !== id)); */
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    const updatedRows = rows.map((row) => (row.id === newRow.id ? updatedRow : row));
    /* onRowChange(name, updatedRows); */
    const isValid = validateRow(newRow);
    if(!isValid){
      setRowModesModel({ ...rowModesModel, [newRow?.id]: { mode: GridRowModes.Edit } });
      /* toast.warning(subscriptioToastMessages.PLEASE_FILL_ONE_COLUM_CRUD,
        { position: toast.POSITION.BOTTOM_RIGHT, theme: "colored" });  */
      return
    }
    setRowModesModel({ ...rowModesModel, [newRow?.id]: { mode: GridRowModes.View } });
    setRows(updatedRows);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  //#region grid columns
  const columns = [
    ...initialColumns,
  ];

  //#region useeffect
   useEffect(() => {
    onRowChange(name, rows);
   }, [rows]);
   
  //#region return
  return (
    <div className='child-margin-10'>
     <Box
      sx={{
        height: height ? height : 400,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}>
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        editMode={readOnly ? "row" : "cell"} // <-- cell editing if not readOnly
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        disableColumnSelector
        slotProps={{ toolbar: { setRows, setRowModesModel } }}
        disableColumnMenu
        hideFooter
        isCellEditable={() => !readOnly} // <-- allow editing if not readOnly
      />
    </Box>
    </div>
  );
}

export default UDataGrid;
